import { RotateCcwIcon, RotateCwIcon, ZoomInIcon, ZoomOutIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { memo, useState, useEffect, useRef, useMemo, useCallback, type PointerEvent as ReactPointerEvent } from "react";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker - use jsdelivr which supports CORS
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.mjs`;

// Alternative fallback if CORS issues persist
// pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.mjs`;

interface Invoice {
  id: string
  original_filename: string
  status: string
  overall_confidence?: number
  processed_at?: string
  mime_type?: string
  client?: {
    id: string
    name: string
    status: string
  }
}

interface FileViewer {
  isLoadingFile: boolean
  fileError: string | null
  fileUrl: string | null
}

interface Zoom {
  zoomLevel: number
  zoomIn: () => void
  zoomOut: () => void
}

interface DocumentViewerProps {
  invoice: Invoice
  fileViewer: FileViewer
  zoom: Zoom
  className?: string
}


// Helper function to determine file type
const getFileType = (invoice: Invoice): 'pdf' | 'image' | 'unknown' => {
  if (invoice.mime_type) {
    if (invoice.mime_type === 'application/pdf') {
      return 'pdf'
    }
    if (invoice.mime_type.startsWith('image/')) {
      return 'image'
    }
  }
  const filename = invoice.original_filename?.toLowerCase() || ''
  if (filename.endsWith('.pdf')) {
    return 'pdf'
  }
  if (filename.match(/\.(png|jpe?g|tiff?)$/)) {
    return 'image'
  }
  return 'unknown'
}

const DocumentViewerComponent = ({
  invoice,
  fileViewer,
  zoom,
  className = ""
}: DocumentViewerProps): JSX.Element => {
  const fileType = getFileType(invoice)
  const documentContainerRef = useRef<HTMLDivElement>(null)
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [rotation, setRotation] = useState(0)
  const pageWrapperRef = useRef<HTMLDivElement | null>(null)
  const imageCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const dragStateRef = useRef({
    startX: 0,
    startY: 0,
    initialX: 0,
    initialY: 0
  })
  const pointerIdRef = useRef<number | null>(null)
  const offsetRef = useRef({ x: 0, y: 0 })
  const [offset, setOffset] = useState(offsetRef.current)

  // PDF event handlers
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setPdfError(null)
  }

  const onDocumentLoadError = (error: Error) => {
    console.error('Error loading PDF:', error)
    setPdfError('Failed to load PDF document')
  }

  // State variables
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 })

  // Calculate optimal PDF size that fits container while maintaining aspect ratio
  const optimalPdfSize = useMemo(() => {
    if (!containerDimensions.width || !containerDimensions.height) {
      return { width: undefined, height: undefined }
    }

    // Account for padding (p-2 = 8px on each side)
    const availableWidth = containerDimensions.width - 16
    const availableHeight = containerDimensions.height - 32

    // Use the maximum available space while maintaining aspect ratio
    // React-pdf will automatically fit within these bounds
    return {
      width: availableWidth,
      height: availableHeight
    }
  }, [containerDimensions.width, containerDimensions.height])

  // Update container dimensions when component mounts or window resizes
  useEffect(() => {
    const updateDimensions = () => {
      if (documentContainerRef.current) {
        const rect = documentContainerRef.current.getBoundingClientRect()
        setContainerDimensions({ width: rect.width, height: rect.height })
      }
    }

    // Initial measurement
    updateDimensions()

    // Add resize listener with debouncing to prevent excessive updates
    let timeoutId: number
    const debouncedUpdate = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(updateDimensions, 100)
    }

    window.addEventListener('resize', debouncedUpdate)

    return () => {
      window.removeEventListener('resize', debouncedUpdate)
      clearTimeout(timeoutId)
    }
  }, [])

  // Reset to page 1 when file changes
  const setBodyUserSelect = (value: string) => {
    if (typeof document !== 'undefined') {
      document.body.style.userSelect = value
    }
  }

  const clampOffset = useCallback((x: number, y: number) => {
    const container = documentContainerRef.current
    const content = pageWrapperRef.current

    if (!container || !content) {
      console.debug('[DocumentViewer] clampOffset (no container/content)', { x, y })
      return { x, y }
    }

    const containerWidth = container.clientWidth
    const containerHeight = container.clientHeight
    const contentWidth = content.scrollWidth || content.offsetWidth || containerWidth
    const contentHeight = content.scrollHeight || content.offsetHeight || containerHeight

    const minX = Math.min(0, containerWidth - contentWidth)
    const maxX = 0
    const minY = Math.min(0, containerHeight - contentHeight)
    const maxY = 0

    return {
      x: Math.min(maxX, Math.max(minX, x)),
      y: Math.min(maxY, Math.max(minY, y))
    }
  }, [])

  const commitOffset = useCallback((x: number, y: number) => {
    const clamped = clampOffset(x, y)
    if (offsetRef.current.x === clamped.x && offsetRef.current.y === clamped.y) {
      return clamped
    }
    offsetRef.current = clamped
    setOffset(clamped)
    return clamped
  }, [clampOffset])

  useEffect(() => {
    if (fileViewer.fileUrl && fileType === 'pdf') {
      setPageNumber(1)
      setNumPages(null)
      setPdfError(null)
    }
    commitOffset(0, 0)
  }, [fileViewer.fileUrl, fileType, commitOffset])

  useEffect(() => {
    const current = offsetRef.current
    commitOffset(current.x, current.y)
  }, [containerDimensions.width, containerDimensions.height, zoom.zoomLevel, pageNumber, rotation, commitOffset])

  const handleRotate = (delta: number) => {
    setRotation((prev) => {
      const total = prev + delta
      const normalized = ((total % 360) + 360) % 360
      return normalized
    })
  }

  useEffect(() => {
    if (fileViewer.fileUrl) {
      setRotation(0)
      imageRef.current = null
    }
  }, [fileViewer.fileUrl])

  // Load source image into an off-screen HTMLImageElement
  useEffect(() => {
    if (!fileViewer.fileUrl || fileType !== 'image') return
    const img = new Image()
    img.onload = () => {
      imageRef.current = img
      drawRotatedImage()
    }
    img.src = fileViewer.fileUrl
  }, [fileViewer.fileUrl, fileType]) // eslint-disable-line react-hooks/exhaustive-deps

  // Draw the image rotated into the canvas — matching how react-pdf renders rotated pages
  const drawRotatedImage = useCallback(() => {
    const img = imageRef.current
    const canvas = imageCanvasRef.current
    if (!img || !canvas || !containerDimensions.width) return

    const nw = img.naturalWidth
    const nh = img.naturalHeight
    const sideways = rotation % 180 !== 0
    const canvasW = sideways ? nh : nw
    const canvasH = sideways ? nw : nh

    // Fit to container width (same as PDF: width={containerWidth})
    const availableWidth = containerDimensions.width - 16
    const scale = availableWidth / canvasW
    const drawW = Math.round(canvasW * scale)
    const drawH = Math.round(canvasH * scale)

    canvas.width = drawW
    canvas.height = drawH

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.clearRect(0, 0, drawW, drawH)
    ctx.save()
    ctx.translate(drawW / 2, drawH / 2)
    ctx.rotate((rotation * Math.PI) / 180)
    // After rotation, draw centered — the source image dimensions are nw×nh
    ctx.drawImage(img, -(nw * scale) / 2, -(nh * scale) / 2, nw * scale, nh * scale)
    ctx.restore()
  }, [rotation, containerDimensions.width, fileType])

  // Re-draw when rotation or container size changes
  useEffect(() => {
    drawRotatedImage()
  }, [drawRotatedImage])

  const handleViewerPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const container = documentContainerRef.current
    if (!container) {
      return
    }

    if (event.pointerType === 'mouse' && event.button !== 0) {
      return
    }

    event.preventDefault()

    setIsDragging(true)
    pointerIdRef.current = event.pointerId
    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      initialX: offsetRef.current.x,
      initialY: offsetRef.current.y
    }

    if (event.currentTarget.hasPointerCapture?.(event.pointerId) !== true) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId)
      } catch (error) {
        // ignore if capture not supported
      }
    }

    if (event.pointerType === 'mouse') {
      setBodyUserSelect('none')
    }

    const content = pageWrapperRef.current
    const logData = {
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      offsetX: offsetRef.current.x,
      offsetY: offsetRef.current.y,
      containerWidth: container.clientWidth,
      containerHeight: container.clientHeight,
      contentWidth: content?.offsetWidth,
      contentHeight: content?.offsetHeight,
      zoom: zoom.zoomLevel
    }
    console.debug('[DocumentViewer] pointerDown', logData)
  }

  const handleViewerPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) {
      return
    }

    event.preventDefault()

    const deltaX = event.clientX - dragStateRef.current.startX
    const deltaY = event.clientY - dragStateRef.current.startY
    const desiredX = dragStateRef.current.initialX + deltaX
    const desiredY = dragStateRef.current.initialY + deltaY

    const applied = commitOffset(desiredX, desiredY)
    console.debug('[DocumentViewer] pointerMove', {
      pointerId: event.pointerId,
      deltaX,
      deltaY,
      desiredX,
      desiredY,
      appliedX: applied.x,
      appliedY: applied.y
    })
  }

  const finishDragging = useCallback(() => {
    const pointerId = pointerIdRef.current
    if (pointerId !== null) {
      const container = documentContainerRef.current
      if (container?.hasPointerCapture?.(pointerId)) {
        container.releasePointerCapture(pointerId)
      }
    }
    pointerIdRef.current = null
    setIsDragging(false)
    setBodyUserSelect('')
  }, [])

  const handleViewerPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) {
      return
    }
    event.preventDefault()
    finishDragging()
  }

  const handleViewerPointerCancel = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) {
      return
    }
    finishDragging()
  }

  useEffect(() => {
    return () => {
      const pointerId = pointerIdRef.current
      if (pointerId !== null) {
        const container = documentContainerRef.current
        if (container?.hasPointerCapture?.(pointerId)) {
          container.releasePointerCapture(pointerId)
        }
        pointerIdRef.current = null
      }
      setBodyUserSelect('')
    }
  }, [])

  return (
    <div className={`col-span-12 lg:col-span-6 flex flex-col lg:sticky lg:top-0 lg:self-start lg:h-screen ${className}`}>
      <Card className="flex min-h-0 w-full flex-1 flex-col overflow-auto rounded-xl shadow-sm">
        <CardContent
          className="flex h-full w-full min-h-0 flex-1 flex-col gap-4 p-4 bg-neutral-950 pt-0"
        >
          <div className="flex flex-col items-start w-full min-h-0 flex-1">
            <div className="w-full bg-neutral-950 border-b border-neutral-950 rounded-tl-lg rounded-tr-lg flex-shrink-0">
              <div className="flex h-14 items-center justify-between px-4 gap-4">
                {/* Left: Document Name */}
                <div className="flex flex-1 items-center min-w-0">
                  <span className="w-fit font-text-sm-medium font-[number:var(--text-sm-medium-font-weight)] text-white text-[length:var(--text-sm-medium-font-size)] tracking-[var(--text-sm-medium-letter-spacing)] leading-[var(--text-sm-medium-line-height)] whitespace-nowrap overflow-hidden text-ellipsis [font-style:var(--text-sm-medium-font-style)]">
                    {invoice.original_filename || 'Document'}
                  </span>
                </div>

                {/* Center: Page Controls (multi-page PDFs only) */}
                {fileType === 'pdf' && numPages && numPages > 1 && (
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                      disabled={pageNumber <= 1}
                      className="px-2 py-1 text-xs bg-gray-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
                    >
                      ‹ Prev
                    </button>
                    <span className="text-xs text-gray-300 min-w-[3rem] text-center">
                      {pageNumber} / {numPages}
                    </span>
                    <button
                      onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                      disabled={pageNumber >= numPages}
                      className="px-2 py-1 text-xs bg-gray-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
                    >
                      Next ›
                    </button>
                  </div>
                )}

                {/* Right: Tool Controls */}
                <div className="flex flex-1 items-center justify-end gap-0">
                  <button onClick={zoom.zoomOut} className="p-2 hover:opacity-80 hover:bg-gray-700 rounded">
                    <ZoomOutIcon className="w-4 h-4 text-white" />
                  </button>

                  <span className="text-sm text-gray-200 min-w-[4rem] text-center">
                    {zoom.zoomLevel}%
                  </span>

                  <button onClick={zoom.zoomIn} className="p-2 hover:opacity-80 hover:bg-gray-700 rounded">
                    <ZoomInIcon className="w-4 h-4 text-white" />
                  </button>

                  <button
                    onClick={() => handleRotate(-90)}
                    className="p-2 hover:opacity-80 hover:bg-gray-700 rounded"
                    aria-label="Rotate counter-clockwise"
                  >
                    <RotateCcwIcon className="w-4 h-4 text-white" />
                  </button>

                  <button
                    onClick={() => handleRotate(90)}
                    className="p-2 hover:opacity-80 hover:bg-gray-700 rounded"
                    aria-label="Rotate clockwise"
                  >
                    <RotateCwIcon className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-1 items-stretch gap-2 w-full overflow-auto min-h-0" ref={documentContainerRef}>
              <div className="flex-1 min-h-0 flex flex-col">
                <div
                  className={`relative h-full w-full min-h-0 overflow-auto rounded border bg-gray-50 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                  style={{ touchAction: 'none' }}
                  onPointerDown={handleViewerPointerDown}
                  onPointerMove={handleViewerPointerMove}
                  onPointerUp={handleViewerPointerUp}
                  onPointerCancel={handleViewerPointerCancel}
                >
                  {fileViewer.isLoadingFile ? (
                    <div className="w-full h-full flex flex-col items-center justify-center">
                      <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
                      <p className="text-gray-300">Loading {fileType === 'pdf' ? 'PDF' : fileType === 'image' ? 'image' : 'document'}...</p>
                    </div>
                  ) : fileViewer.fileError ? (
                    <div className="w-full h-full flex items-center justify-center text-red-500">
                      <div className="text-center">
                        <p className="text-lg font-medium">Error Loading {fileType === 'pdf' ? 'PDF' : fileType === 'image' ? 'Image' : 'Document'}</p>
                        <p className="text-sm">{fileViewer.fileError}</p>
                        <button
                          onClick={() => window.location.reload()}
                          className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Retry
                        </button>
                      </div>
                    </div>
                  ) : fileViewer.fileUrl ? (
                    fileType === 'pdf' ? (
                      <div className="w-full h-full flex-1">
                        <div
                          className="relative inline-block"
                          ref={pageWrapperRef}
                          style={{
                            position: 'relative',
                            left: `${offset.x}px`,
                            top: `${offset.y}px`,
                            willChange: isDragging ? 'left, top' : undefined
                          }}
                        >
                          <Document
                            file={fileViewer.fileUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={onDocumentLoadError}
                            loading={
                              <div className="w-full h-full flex flex-col items-center justify-center">
                                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
                                <p className="text-gray-300">Loading PDF...</p>
                              </div>
                            }
                            error={
                              <div className="w-full h-full flex items-center justify-center text-red-500">
                                <div className="text-center">
                                  <p className="text-lg font-medium">Error Loading PDF</p>
                                  <p className="text-sm">{pdfError || 'Failed to load PDF document'}</p>
                                </div>
                              </div>
                            }
                          >
                            <Page
                              pageNumber={pageNumber}
                              scale={zoom.zoomLevel / 100}
                              width={optimalPdfSize.width}
                              height={optimalPdfSize.height}
                              rotate={rotation}
                              loading={
                                <div className="w-full h-full flex items-center justify-center">
                                  <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                                </div>
                              }
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                          </Document>
                        </div>
                      </div>
                    ) : fileType === 'image' ? (
                      <div className="w-full h-full flex-1">
                        <div
                          className="relative inline-block"
                          ref={pageWrapperRef}
                          style={{
                            position: 'relative',
                            left: `${offset.x}px`,
                            top: `${offset.y}px`,
                            willChange: isDragging ? 'left, top' : undefined
                          }}
                        >
                          <canvas
                            ref={imageCanvasRef}
                            style={{
                              display: 'block',
                              transform: zoom.zoomLevel !== 100 ? `scale(${zoom.zoomLevel / 100})` : undefined,
                              transformOrigin: 'top left',
                              transition: 'transform 0.2s ease',
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-yellow-600">
                        <div className="text-center">
                          <p className="text-lg font-medium">Unsupported File Type</p>
                          <p className="text-sm">This file type cannot be displayed in the browser</p>
                          <button
                            onClick={() => fileViewer.fileUrl && window.open(fileViewer.fileUrl, '_blank')}
                            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                          >
                            Download File
                          </button>
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500">
                      <div className="text-center">
                        <p className="text-lg font-medium">No Document Available</p>
                        <p className="text-sm">Unable to load document</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export const DocumentViewer = memo(DocumentViewerComponent, (prev, next) => {
  if (prev.className !== next.className) return false
  if (prev.invoice.id !== next.invoice.id) return false
  if (prev.invoice.original_filename !== next.invoice.original_filename) return false
  if (prev.invoice.mime_type !== next.invoice.mime_type) return false
  if (prev.fileViewer.fileUrl !== next.fileViewer.fileUrl) return false
  if (prev.fileViewer.isLoadingFile !== next.fileViewer.isLoadingFile) return false
  if (prev.fileViewer.fileError !== next.fileViewer.fileError) return false
  if (prev.zoom.zoomLevel !== next.zoom.zoomLevel) return false
  return true
})
